import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Link } from 'react-router';
import { useGetEventTwin } from '@cdf/api-client-react';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  BedDoubleIcon,
  BoxIcon,
  Minimize2Icon,
  MountainIcon,
  Rotate3DIcon,
  ScanLineIcon,
  SparklesIcon,
  UsersRoundIcon,
  XIcon,
} from 'lucide-react';
import {
  CAMERA_PRESETS,
  FLOOR_BOUNDS,
  WOODSMOKE_NAV_GRAPH,
  type CameraPreset,
  type RoomDef,
} from '@/components/twin/woodsmoke-twin-data';
import {
  WoodsmokeTwinScene,
  isWoodsmokeWebGLAvailable,
  type WoodsmokeTwinCameraController,
} from '@/components/twin/woodsmoke-twin-scene';
import {
  ALL_PEDESTRIAN_ROLES,
  createPedestrian,
  PedestrianSimulation,
  type PedestrianMeta,
  type PedestrianRole,
  type PedestrianState,
} from '@/components/twin/pedestrian-simulation';
import { PedestrianControls } from '@/components/twin/pedestrian-controls';
import { TwinAvatarCustomizer } from '@/components/twin/twin-avatar-customizer';
import { useLivePresencePedestrians } from '@/components/twin/use-live-presence-pedestrians';
import { DesktopTwinPanel } from '@/components/twin/desktop-twin-panel';
import { ZoomPanControls } from '@/components/ui/zoom-pan-controls';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useFullscreenTwin } from '@/hooks/use-fullscreen-twin';
import { useTwinPresence } from '@/hooks/use-twin-presence';

const WOODSMOKE_EVENT_SLUG = 'alpine-woodsmoke-unit-4';
const MOBILE_VIEWPORT_QUERY = '(max-width: 639px), (max-width: 935px) and (orientation: landscape), (max-height: 500px)';
const FALLBACK_TWIN = {
  title: 'Alpine Woodsmoke Unit 4',
  summary: 'Explore a warm three-bedroom alpine apartment with a stone fireplace, family spaces and valley balcony.',
} as const;

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
}

function RoomDetails({ room }: { room: RoomDef }) {
  const sleepingLabel = room.sleepingCapacity === 0
    ? 'Amenity space'
    : `${room.sleepingCapacity} ${room.sleepingCapacity === 1 ? 'guest' : 'guests'}`;

  return (
    <div className="space-y-4">
      <p className="leading-relaxed text-muted-foreground">{room.description}</p>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/45 px-3 py-2 text-sm">
        <BedDoubleIcon className="size-4 text-primary" aria-hidden />
        <span className="font-medium">{sleepingLabel}</span>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amenities</p>
        <div className="flex flex-wrap gap-2">
          {room.amenities.map((amenity) => <Badge key={amenity} variant="secondary">{amenity}</Badge>)}
        </div>
      </div>
    </div>
  );
}

export function WoodsmokeTwinPage() {
  const eventTwinQuery = useGetEventTwin(WOODSMOKE_EVENT_SLUG);
  const [selectedRoom, setSelectedRoom] = useState<RoomDef | null>(null);
  const [hoveredRoom, setHoveredRoom] = useState<RoomDef | null>(null);
  const [presetId, setPresetId] = useState<CameraPreset['id']>('overview');
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [pedestriansEnabled, setPedestriansEnabled] = useState(true);
  const [pedestrianSpeed, setPedestrianSpeed] = useState(1);
  const [showPedestrianPaths, setShowPedestrianPaths] = useState(false);
  const [visiblePedestrianRoles, setVisiblePedestrianRoles] = useState<PedestrianRole[]>(() => [...ALL_PEDESTRIAN_ROLES]);
  const [pedestrianCount, setPedestrianCount] = useState(0);
  const [selectedPedestrian, setSelectedPedestrian] = useState<PedestrianMeta | null>(null);
  const pedestriansRef = useRef<PedestrianState[]>([]);
  const cameraRef = useRef<WoodsmokeTwinCameraController>(null);
  const fullscreen = useFullscreenTwin();
  const webglAvailable = useMemo(() => isWoodsmokeWebGLAvailable(), []);
  const presence = useTwinPresence({
    sceneId: 'accommodation:alpine-woodsmoke-unit-4',
    // Presence is intentionally placed at the lounge when a device shares a GPS
    // location: the apartment is indoors and has no GPS-to-floor calibration.
    projectLocation: () => ({
      floorX: 0,
      floorZ: 8,
      accuracyMeters: null,
    }),
  });

  const projectPresence = useCallback((participant: { floorX?: number | null; floorZ?: number | null }) => {
    if (participant.floorX == null || participant.floorZ == null) return null;
    return new THREE.Vector3(
      THREE.MathUtils.clamp(participant.floorX, FLOOR_BOUNDS.minX + 1, FLOOR_BOUNDS.maxX - 1),
      0,
      THREE.MathUtils.clamp(participant.floorZ, FLOOR_BOUNDS.minZ + 1, FLOOR_BOUNDS.maxZ - 1),
    );
  }, []);
  const ownLivePresence = useMemo(() => {
    if (!presence.isSharing || !presence.locationTrack.location) return null;
    return {
      userId: 'local:accommodation:alpine-woodsmoke-unit-4',
      displayName: presence.displayName,
      avatarColor: presence.avatarColor,
      accuracyMeters: presence.locationTrack.location.accuracyMeters,
      position: new THREE.Vector3(0, 0, 8),
    };
  }, [presence.avatarColor, presence.displayName, presence.isSharing, presence.locationTrack.location]);
  const livePedestrians = useLivePresencePedestrians(presence.participants, projectPresence, ownLivePresence);
  const scenePedestrians = useMemo(
    () => pedestriansEnabled ? [...pedestriansRef.current, ...livePedestrians] : [],
    [livePedestrians, pedestrianCount, pedestriansEnabled],
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  useEffect(() => {
    if (selectedPedestrian && !visiblePedestrianRoles.includes(selectedPedestrian.role)) {
      setSelectedPedestrian(null);
    }
  }, [selectedPedestrian, visiblePedestrianRoles]);

  const currentPreset = CAMERA_PRESETS.find((preset) => preset.id === presetId) ?? CAMERA_PRESETS[0]!;
  const title = eventTwinQuery.data?.twin.title ?? FALLBACK_TWIN.title;
  const summary = eventTwinQuery.data?.twin.summary ?? FALLBACK_TWIN.summary;
  const isFullscreenLandscape = fullscreen.isFullscreen && fullscreen.isLandscape;

  const handleSpawnPedestrian = useCallback(() => {
    const pedestrian = createPedestrian(FLOOR_BOUNDS, { navGraph: WOODSMOKE_NAV_GRAPH });
    pedestriansRef.current = [...pedestriansRef.current, pedestrian];
    setPedestrianCount(pedestriansRef.current.length);
  }, []);
  const handleResetPedestrians = useCallback(() => {
    pedestriansRef.current = [];
    setPedestrianCount(0);
    setSelectedPedestrian(null);
  }, []);
  const handleSelectRoom = useCallback((room: RoomDef | null) => {
    setSelectedRoom(room);
    if (room) setSelectedPedestrian(null);
  }, []);

  if (!webglAvailable) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertCircleIcon className="size-5 text-amber-500" />3D not available</CardTitle></CardHeader>
          <CardContent className="space-y-4"><p className="text-sm text-muted-foreground">This browser does not support WebGL, which is needed to explore the apartment in 3D.</p><Button asChild variant="outline"><Link to="/twin">Back to Digital Twin Hub</Link></Button></CardContent>
        </Card>
      </div>
    );
  }

  const containerClassName = fullscreen.isFallbackFullscreen
    ? 'fixed inset-0 z-50 h-[100dvh] w-screen overflow-hidden bg-slate-950'
    : fullscreen.isFullscreen
      ? 'relative h-full w-full overflow-hidden bg-slate-950'
      : 'relative h-[calc(100vh-4rem)] min-h-[620px] overflow-hidden bg-slate-950';

  return (
    <div className={isFullscreenLandscape ? 'h-[100dvh] overflow-hidden bg-slate-950' : 'flex h-[calc(100vh-4rem)] flex-col'}>
      {!isFullscreenLandscape && (
        <header className="z-20 flex flex-wrap items-center gap-3 border-b border-border bg-background/95 px-4 py-2 backdrop-blur">
          <Button asChild variant="ghost" size="icon" aria-label="Return to digital twins"><Link to="/twin"><ArrowLeftIcon className="size-4" /></Link></Button>
          <div className="flex min-w-0 items-center gap-2"><MountainIcon className="size-4 shrink-0 text-primary" /><h1 className="truncate text-sm font-semibold">{title} — 3D Apartment Twin</h1></div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5"><Button asChild size="sm" variant="outline" className="h-7 px-2.5 text-xs"><Link to={`/hunt/${WOODSMOKE_EVENT_SLUG}`}><ScanLineIcon className="mr-1 size-3.5" />Image Hunt</Link></Button><TwinAvatarCustomizer sceneLabel={title} presence={presence} />{CAMERA_PRESETS.map((preset) => <Button key={preset.id} type="button" size="sm" variant={preset.id === presetId ? 'default' : 'outline'} className="h-7 px-2.5 text-xs" onClick={() => setPresetId(preset.id)}>{preset.label}</Button>)}</div>
        </header>
      )}

      <div ref={fullscreen.containerRef} className={containerClassName}>
        {isFullscreenLandscape && (
          <div className="absolute inset-x-0 top-0 z-50 flex min-h-14 items-center justify-between gap-3 border-b border-border bg-background/95 px-3 py-1.5 backdrop-blur">
            <p className="min-w-0 truncate text-sm font-semibold">{title}</p>
            <div className="flex shrink-0 items-center gap-2"><TwinAvatarCustomizer sceneLabel={title} presence={presence} /><Button type="button" size="sm" variant="secondary" className="h-10 gap-1.5" onClick={() => void fullscreen.exitFullscreen()}><Minimize2Icon className="size-4" /><span className="hidden sm:inline">Exit</span></Button></div>
          </div>
        )}

        <WoodsmokeTwinScene ref={cameraRef} cameraPreset={currentPreset} selectedRoomId={selectedRoom?.id ?? null} onSelect={handleSelectRoom} onHoverChange={setHoveredRoom}>
          {scenePedestrians.length > 0 && <PedestrianSimulation pedestrians={scenePedestrians} bounds={FLOOR_BOUNDS} navGraph={WOODSMOKE_NAV_GRAPH} showPaths={showPedestrianPaths} visibleRoles={visiblePedestrianRoles} selectedId={selectedPedestrian?.id} onSelect={setSelectedPedestrian} speedMultiplier={pedestrianSpeed} />}
        </WoodsmokeTwinScene>

        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-4 p-4 sm:p-6">
          <Card className="pointer-events-auto hidden max-w-sm border-white/30 bg-background/90 shadow-xl backdrop-blur lg:block"><CardHeader className="space-y-2 pb-3"><CardTitle className="flex items-center gap-2 text-lg"><SparklesIcon className="size-4 text-amber-500" />Mountain living, mapped in 3D</CardTitle><p className="text-sm leading-relaxed text-muted-foreground">{summary}</p></CardHeader><CardContent className="flex items-center gap-2 text-xs text-muted-foreground"><Rotate3DIcon className="size-4" />Drag to explore · Select a room for its amenities.</CardContent></Card>
          {hoveredRoom && !selectedRoom && <div className="pointer-events-none ml-auto rounded-full border border-white/40 bg-background/85 px-3 py-1.5 text-xs font-medium shadow-lg backdrop-blur">{hoveredRoom.name}</div>}
        </div>

        <ZoomPanControls onZoomIn={() => cameraRef.current?.zoomIn()} onZoomOut={() => cameraRef.current?.zoomOut()} onPan={(direction) => cameraRef.current?.pan(direction)} onReset={() => cameraRef.current?.reset()} onToggleFullscreen={() => void fullscreen.toggleFullscreen()} isFullscreen={fullscreen.isFullscreen} className={isFullscreenLandscape ? '!top-16 !bottom-auto' : undefined} />
        <PedestrianControls enabled={pedestriansEnabled} onToggle={setPedestriansEnabled} count={pedestrianCount} onSpawn={handleSpawnPedestrian} onReset={handleResetPedestrians} speed={pedestrianSpeed} onSpeedChange={setPedestrianSpeed} showPaths={showPedestrianPaths} onShowPathsChange={setShowPedestrianPaths} visibleRoles={visiblePedestrianRoles} onVisibleRolesChange={setVisiblePedestrianRoles} selectedPedestrian={selectedPedestrian} onDeselectPedestrian={() => setSelectedPedestrian(null)} className={isFullscreenLandscape ? 'top-16' : undefined} mobileClassName={isFullscreenLandscape ? 'top-16 z-30' : undefined} mobileSheetClassName={isFullscreenLandscape ? 'max-h-[calc(100dvh-4rem)]' : undefined} mobileMode={isMobile} />

        {!isMobile && selectedRoom && <DesktopTwinPanel title={selectedRoom.name} icon={BoxIcon} className={isFullscreenLandscape ? 'right-4 top-16 z-40 max-h-[calc(100%-5rem)] overflow-auto' : 'right-4 top-4 z-40 max-h-[calc(100%-2rem)] overflow-auto'} panelClassName="w-80 shadow-xl" contentClassName="p-1 text-sm" headerActions={<Button type="button" variant="ghost" size="icon" className="size-8 shrink-0 rounded-lg" aria-label="Close room details" onClick={() => setSelectedRoom(null)}><XIcon className="size-4" /></Button>}><RoomDetails room={selectedRoom} /></DesktopTwinPanel>}
        {isMobile && selectedRoom && <Sheet open onOpenChange={(open) => { if (!open) setSelectedRoom(null); }}><SheetContent side="bottom" className={isFullscreenLandscape ? 'max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]' : 'max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]'} onPointerDown={(event) => event.stopPropagation()}><SheetHeader><SheetTitle>{selectedRoom.name}</SheetTitle><SheetDescription>Sleeping, amenity and layout details for this apartment zone.</SheetDescription></SheetHeader><div className="mt-5 text-sm"><RoomDetails room={selectedRoom} /></div></SheetContent></Sheet>}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-4 sm:p-6"><div className="pointer-events-auto flex w-fit max-w-full items-center gap-2 rounded-full border border-white/40 bg-background/90 px-3 py-2 text-xs text-muted-foreground shadow-lg backdrop-blur"><UsersRoundIcon className="size-4 text-primary" />Add simulated visitors or share a live avatar from the controls.</div></div>
        {eventTwinQuery.isError && <div className="pointer-events-none absolute bottom-20 left-4 z-20 hidden rounded-lg border bg-background/90 px-3 py-2 text-xs text-muted-foreground shadow-lg backdrop-blur sm:block">Using the apartment preview while live catalog details reconnect.</div>}
      </div>
    </div>
  );
}
